<template>
  <div class="actuals-page-root">
    <div class="actuals-page-header-banner">
      <div class="actuals-page-header-row">
        <span class="actuals-page-header-title">Bookings & Actuals</span>
        <span class="actuals-page-header-actions">
          <b-button
            v-if="!this.$route.query.booking_id"
            size="sm"
            variant="danger"
            class="actuals-error-summary-btn"
            title="Summarize error categories for the current date range"
            v-on:click.prevent="open_error_summary_modal()"
          >
            <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
            <span class="actuals-error-summary-count">{{ error_cnt }}</span>
          </b-button>
          <b-button size="sm" variant="danger" title="View actuals errors grouped by cause with suggested fixes"
                    :to="{ path: '/actuals_troubleshooter', query: { source: 'cm', fromDate: details_fromDate, toDate: details_toDate, filter: actuals_filter, company: selected_company, type: selected_type } }">
            <i class="fa fa-exclamation-triangle" style="margin-right: 4px;"></i> <b>Troubleshoot</b>
          </b-button>
          <b-button size="sm" variant="info" title="Analyze HOS time accuracy vs planned times for successful actuals"
                    :to="{ path: '/actuals_stats', query: { fromDate: details_fromDate, toDate: details_toDate, filter: actuals_filter, company: selected_company, type: selected_type } }"
                    style="margin-left: 8px;">
            <i class="fa fa-line-chart" style="margin-right: 4px;"></i> <b>Stats</b>
          </b-button>
          <b-button
            v-if="!this.$route.query.booking_id"
            size="sm"
            variant="primary"
            title="Reprocess actuals for the current date range and filters"
            style="margin-left: 8px;"
            v-on:click.prevent="reprocess_actuals()"
          >
            <i class="fa fa-refresh" style="margin-right: 4px;"></i> <b>Reprocess</b>
          </b-button>
          <actuals-troubleshooter-help source="cm"/>
        </span>
      </div>
    </div>

    <div class="actuals-cm-toolbar">
      <b-button-group size="sm" class="actuals-quick-range">
        <b-button
          v-for="opt in quick_range_options"
          :key="opt.value"
          :variant="quick_range_active_days === opt.value ? 'primary' : 'outline-secondary'"
          v-on:click.prevent="set_quick_range(opt.value)"
        >{{ opt.label }}</b-button>
      </b-button-group>
      <b-button-group class="toolbar-date-shifts">
        <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" :title="base_required ? 'Previous week' : 'Previous month'" v-on:click.prevent="shift_detail_graph_date(-1, 'months')"><i class="fa fa-angle-double-left" aria-hidden="true"></i></b-button>
        <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Previous day" v-on:click.prevent="shift_detail_graph_date(-1, 'days')"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
      </b-button-group>
      <span class="toolbar-date-wrap">
        <date-picker class="toolbar-date" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"/>
      </span>
      <span class="toolbar-label">to</span>
      <span class="toolbar-date-wrap">
        <date-picker class="toolbar-date" v-model="details_toDate" :config="options" :placeholder="details_toDate"/>
      </span>
      <b-button-group class="toolbar-date-shifts">
        <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Next day" v-on:click.prevent="shift_detail_graph_date(1, 'days')"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
        <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" :title="base_required ? 'Next week' : 'Next month'" v-on:click.prevent="shift_detail_graph_date(1, 'months')"><i class="fa fa-angle-double-right" aria-hidden="true"></i></b-button>
      </b-button-group>
      <b-form-select size="sm" v-model="actuals_filter" :options="actuals_filter_options" class="toolbar-select"/>
      <b-form-select size="sm" v-if="company_options" v-model="selected_company" :options="company_options" class="toolbar-select toolbar-company"/>
      <b-form-select size="sm" v-if="type_options" v-model="selected_type" :options="type_options" class="toolbar-select toolbar-type"/>
      <b-button size="sm" v-on:click.prevent="search_by_date_range()">Search</b-button>
      <span class="toolbar-group">
        <span class="toolbar-label">By ID</span>
        <b-form-input size="sm" class="toolbar-input" v-model="booking_search" placeholder="charterID"/>
        <b-button size="sm" v-on:click.prevent="search_by_id()">Search</b-button>
      </span>
      <b-form-select size="sm" v-if="vehicle_options" v-model="selected_vehicle" :options="vehicle_options" class="toolbar-select toolbar-vehicle"/>
      <b-form-select size="sm" v-if="show_base_select" v-model="selected_base" :options="base_options" class="toolbar-select toolbar-base"/>
      <b-button size="sm" variant="success" @click="download" class="toolbar-btn">Download</b-button>
    </div>

    <div v-if="!this.$route.query.booking_id && !loading" class="actuals-cm-filter-area">
      <div v-if="summary.actuals_request_timestamp" class="actuals-cm-meta-line">
        <b>actuals last requested in CM for {{details_toDate}} at {{ summary.actuals_request_timestamp | formatDateTimeCustom(summary.datetime_format)}}</b>
      </div>
      <div class="actuals-cm-status-filter-row">
        <div class="actuals-filter-chips actuals-cm-filter-chips">
        <span class="actuals-status-filter-center-group">
        <span
          @click="filter_by_status(null)"
          class="actuals-chip actuals-chip-clickable actuals-chip-total"
          :class="{'actuals-chip-selected': selected_status == null}"
        >{{ items_row_count }}/{{items_full.length}}</span>
        <span class="actuals-filter-sep">|</span>
        <span class="actuals-filter-label">Status</span>
        <span
          @click="filter_by_status('success')"
          class="actuals-chip actuals-chip-clickable success"
          :class="{'actuals-chip-selected': selected_status == 'success'}"
        >Success {{success_cnt}}</span>
        <span
          @click="filter_by_status('error')"
          class="actuals-chip actuals-chip-clickable error"
          :class="{'actuals-chip-selected': selected_status == 'error'}"
        >Errors {{error_cnt}}</span>
        <template v-if="show_extra_status_filters">
          <span
            @click="filter_by_status('cancelled')"
            class="actuals-chip actuals-chip-clickable cancelled"
            :class="{'actuals-chip-selected': selected_status == 'cancelled'}"
          >Cancelled {{cancelled_cnt}}</span>
          <span
            @click="filter_by_status('skipped')"
            class="actuals-chip actuals-chip-clickable skipped"
            :class="{'actuals-chip-selected': selected_status == 'skipped'}"
          >Skipped {{skipped_cnt}}</span>
          <span
            @click="filter_by_status('unprocessed')"
            class="actuals-chip actuals-chip-clickable unprocessed"
            :class="{'actuals-chip-selected': selected_status == 'unprocessed'}"
          >Unprocessed {{unprocessed_cnt}}</span>
        </template>
        <span
          @click="show_extra_status_filters = !show_extra_status_filters"
          class="filter-expander"
          :title="show_extra_status_filters ? 'Hide cancelled/skipped/unprocessed filters' : 'Show cancelled/skipped/unprocessed filters'"
          role="button"
        ><i :class="show_extra_status_filters ? 'fa fa-angle-left' : 'fa fa-angle-right'" aria-hidden="true"></i></span>
        <span class="actuals-filter-sep">|</span>
        <span class="actuals-filter-chip-group actuals-trip-duration-filter-group">
          <span class="actuals-filter-label" :title="tripDurationFilterTooltips.group">Trip</span>
          <span
            @click="filter_by_trip_duration('single')"
            class="actuals-chip actuals-chip-clickable actuals-trip-duration-chip trip-single"
            :class="{'actuals-chip-selected': selected_trip_duration == 'single'}"
            :title="tripDurationFilterTooltips.single"
          >Single {{ trip_single_cnt }}</span>
          <span
            @click="filter_by_trip_duration('multiday')"
            class="actuals-chip actuals-chip-clickable actuals-trip-duration-chip trip-multiday"
            :class="{'actuals-chip-selected': selected_trip_duration == 'multiday'}"
            :title="tripDurationFilterTooltips.multiday"
          >Multiday {{ trip_multiday_cnt }}</span>
        </span>
        <template v-if="show_overlap_filters">
          <span class="actuals-filter-sep">|</span>
          <span class="actuals-filter-chip-group actuals-overlap-filter-group">
            <span class="actuals-filter-label">Overlap</span>
            <span
              v-if="driver_overlap_cnt > 0"
              @click="filter_by_status('driver_overlap')"
              class="actuals-chip actuals-chip-clickable overlap-driver"
              :class="{'actuals-chip-selected': selected_status == 'driver_overlap'}"
              title="Driver HOS on/off duty times overlap another booking"
            >By Driver {{driver_overlap_cnt}}</span>
            <span
              v-if="vehicle_overlap_cnt > 0"
              @click="filter_by_status('vehicle_overlap')"
              class="actuals-chip actuals-chip-clickable overlap-vehicle"
              :class="{'actuals-chip-selected': selected_status == 'vehicle_overlap'}"
              title="Vehicle start/finish location times overlap another booking"
            >By Vehicle {{vehicle_overlap_cnt}}</span>
            <span
              v-if="overlap_correction_cnt > 0"
              @click="filter_by_status('overlap_correction')"
              class="actuals-chip actuals-chip-clickable overlap-correction"
              :class="{'actuals-chip-selected': selected_status == 'overlap_correction'}"
              title="Bookings with overlap allocator corrections applied"
            >Corrected {{overlap_correction_cnt}}</span>
          </span>
        </template>
        <span class="actuals-filter-sep">|</span>
        <span class="actuals-filter-chip-group actuals-booking-filter-group">
          <span class="actuals-filter-label">Filter</span>
          <b-form-input size="sm" class="actuals-booking-filter-input" v-model="booking_filter" placeholder=""/>
        </span>
        </span>
        </div>
        <span v-if="selected_status === 'error'" class="actuals-error-filter-group">
          <span class="actuals-error-filter-label">Error category:</span>
          <b-form-select size="sm" v-model="selected_error_category" :options="error_category_options" class="actuals-error-filter-select"/>
          <b-button v-if="selected_error_category" size="sm" variant="link" class="actuals-error-filter-clear" v-on:click.prevent="selected_error_category = null">clear</b-button>
        </span>
        <div v-if="feedback_audit_mode_enabled" class="actuals-filter-chips actuals-cm-filter-chips actuals-feedback-filter-row">
          <span class="actuals-filter-chip-group actuals-feedback-filter-group">
            <span class="actuals-filter-label" :title="feedbackFilterTooltips.group">Feedback</span>
            <span
              @click="filter_by_feedback('positive')"
              class="actuals-chip actuals-chip-clickable actuals-feedback-chip feedback-positive"
              :class="{'actuals-chip-selected': selected_feedback == 'positive'}"
              :title="feedbackFilterTooltips.positive"
            ><i class="fa fa-check" aria-hidden="true"></i> {{ feedback_positive_cnt }}</span>
            <span
              @click="filter_by_feedback('negative')"
              class="actuals-chip actuals-chip-clickable actuals-feedback-chip feedback-negative"
              :class="{'actuals-chip-selected': selected_feedback == 'negative'}"
              :title="feedbackFilterTooltips.negative"
            ><i class="fa fa-thumbs-down" aria-hidden="true"></i> {{ feedback_negative_cnt }}</span>
            <span
              @click="filter_by_feedback('concern')"
              class="actuals-chip actuals-chip-clickable actuals-feedback-chip feedback-concern"
              :class="{'actuals-chip-selected': selected_feedback == 'concern'}"
              :title="feedbackFilterTooltips.concern"
            ><i class="fa fa-flag" aria-hidden="true"></i> {{ feedback_concern_cnt }}</span>
            <span
              @click="filter_by_feedback('none')"
              class="actuals-chip actuals-chip-clickable actuals-feedback-chip feedback-none"
              :class="{'actuals-chip-selected': selected_feedback == 'none'}"
              :title="feedbackFilterTooltips.none"
            ><i class="fa fa-circle-o" aria-hidden="true"></i> {{ feedback_none_cnt }}</span>
            <span
              @click="confirm_clear_feedback_for_date_range"
              class="actuals-chip actuals-chip-clickable actuals-feedback-clear-btn"
              :class="{'actuals-chip-disabled': clearing_feedback}"
              :title="feedbackFilterTooltips.clear"
              role="button"
              aria-label="Clear all feedback for this date range"
            ><i class="fa fa-times" aria-hidden="true"></i></span>
          </span>
        </div>
      </div>
    </div>

    <div>
      <b-table ref="actualsCmTable" :key="actuals_table_key" :busy="loading" :sticky-header="actuals_sticky_header" bordered striped
               table-class="actuals-cm-table" :items="items" :fields="table_fields"
               :current-page="currentPage" :tbody-tr-class="get_row_color"
               :per-page="perPage">

        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>

        <template slot="bookingID" slot-scope="row">
          <span v-if="!row.item._overlap_group_header" class="actuals-id-cell">
            <a href="#" class="actuals-id-copy" @click.prevent="copy_booking_id(row.item.bookingID)"
               :title="copied_booking_id === row.item.bookingID ? 'Copied!' : ('Copy ID: ' + row.item.bookingID)">
              <i :class="copied_booking_id === row.item.bookingID ? 'fa fa-check' : 'fa fa-copy'" aria-hidden="true"></i>
            </a>
            <router-link
              v-if="isEffectiveAdmin"
              target="_blank"
              class="actuals-id-text actuals-id-link"
              :to="booking_search_route(row.item.bookingID)"
              :title="'Search for ' + row.item.bookingID"
            >{{ row.item.bookingID }}</router-link>
          </span>
        </template>

        <template slot="charterID" slot-scope="row">
          <span v-if="row.item._overlap_group_header"></span>
          <span v-else class="actuals-id-cell">
            <router-link
              target="_blank"
              class="actuals-id-link"
              :to="booking_search_route(row.item.charterID)"
              :title="'Search for ' + row.item.charterID"
            >{{ row.item.charterID }}</router-link>
          </span>
        </template>

        <template slot="status_msg" slot-scope="row">
          <div v-if="row.item._overlap_group_header"></div>
          <div v-else class="actuals-status-msg-body">
            <template v-if="show_overlap_time_highlights(row.item)">
                <div
                  v-if="row.item.actual.nearestLoginStartDateTime || row.item.actual.nearestLogoutFinishDateTime"
                  class="actuals-status-msg-line"
                >
                  <span class="actuals-status-msg-label">Driver</span>
                  <span :class="hos_time_highlight_classes(row.item, 'start', row.item.driver_overlap_highlight_start)">{{ formatActualTimeForStatus(row.item.actual.nearestLoginStartDateTime) || '—' }}</span>
                  –
                  <span :class="hos_time_highlight_classes(row.item, 'finish', row.item.driver_overlap_highlight_end)">{{ formatActualTimeForStatus(row.item.actual.nearestLogoutFinishDateTime) || '—' }}</span>
                </div>
                <div
                  v-if="row.item.actual.nearestLocationStartDateTime || row.item.actual.nearestLocationFinishDateTime"
                  class="actuals-status-msg-line"
                >
                  <span class="actuals-status-msg-label">Vehicle</span>
                  <span :class="{'overlap-time': row.item.vehicle_overlap_highlight_start}">{{ formatActualTimeForStatus(row.item.actual.nearestLocationStartDateTime) || '—' }}</span>
                  –
                  <span :class="{'overlap-time': row.item.vehicle_overlap_highlight_end}">{{ formatActualTimeForStatus(row.item.actual.nearestLocationFinishDateTime) || '—' }}</span>
                </div>
                <div
                  v-for="line in success_status_lines_without_overlap(row.item)"
                  :key="'overlap-line-' + line.key"
                  class="actuals-status-msg-line"
                >
                  <template v-if="line.type === 'range'">
                    <span class="actuals-status-msg-label">{{ line.label }}</span>
                    <span v-if="line.label === 'Driver'" :class="hos_time_highlight_classes(row.item, 'start', false)">{{ formatActualTimeForStatus(line.start) || '—' }}</span>
                    <template v-else>{{ formatActualTimeForStatus(line.start) || '—' }}</template>
                    –
                    <span v-if="line.label === 'Driver'" :class="hos_time_highlight_classes(row.item, 'finish', false)">{{ formatActualTimeForStatus(line.end) || '—' }}</span>
                    <template v-else>{{ formatActualTimeForStatus(line.end) || '—' }}</template>
                  </template>
                  <template v-else><span v-html="line.text"></span></template>
                </div>
                <div
                  v-for="(extra, idx) in success_status_extras(row.item)"
                  :key="'overlap-extra-' + idx"
                  class="actuals-status-msg-line"
                  v-html="extra"
                ></div>
              </template>
              <template v-else-if="row.item.status === 'success' && row.item.actual">
                <div
                  v-for="line in format_success_status_lines(row.item)"
                  :key="'success-line-' + line.key"
                  class="actuals-status-msg-line"
                >
                  <template v-if="line.type === 'range'">
                    <span class="actuals-status-msg-label">{{ line.label }}</span>
                    <span v-if="line.label === 'Driver'" :class="hos_time_highlight_classes(row.item, 'start', false)">{{ formatActualTimeForStatus(line.start) || '—' }}</span>
                    <template v-else>{{ formatActualTimeForStatus(line.start) || '—' }}</template>
                    –
                    <span v-if="line.label === 'Driver'" :class="hos_time_highlight_classes(row.item, 'finish', false)">{{ formatActualTimeForStatus(line.end) || '—' }}</span>
                    <template v-else>{{ formatActualTimeForStatus(line.end) || '—' }}</template>
                  </template>
                  <template v-else><span v-html="line.text"></span></template>
                </div>
                <div
                  v-for="(extra, idx) in success_status_extras(row.item)"
                  :key="'success-extra-' + idx"
                  class="actuals-status-msg-line"
                  v-html="extra"
                ></div>
              </template>
              <template v-else-if="row.item.status_msg">
                <div
                  v-for="(msg, idx) in non_success_status_msg_parts(row.item)"
                  :key="'status-msg-' + idx"
                  class="actuals-status-msg-line"
                >
                  <error-category-popover v-if="row.item.status === 'error'" :message="msg" source="cm"/>
                  <span v-html="msg"/>
                </div>
              </template>
          </div>
        </template>

        <template slot="actuals_chart" slot-scope="row">
          <div v-if="row.item._overlap_group_header"></div>
          <div
            v-else
            class="actuals-details-cell actuals-details-cell--stacked"
          >
            <button
              type="button"
              class="actuals-details-icon-btn"
              title="Show Actuals Details"
              :disabled="hos_modal_loading"
              @click.stop="open_hos_modal(row.item)"
            >
              <i class="fa fa-area-chart" aria-hidden="true"></i>
            </button>
            <overlap-correction-icon-btn
              v-if="has_overlap_correction(row.item)"
              @click="open_overlap_updates_modal(row.item)"
            />
            <actuals-feedback-controls
              :item="row.item"
              :tenant="user.tenant"
              :audit-mode="feedback_audit_mode_enabled"
              @showAlert="(msg, variant, timeout) => $emit('showAlert', msg, variant, timeout)"
              @feedback-saved="on_feedback_saved"
            />
          </div>
        </template>

        <template slot="notes" slot-scope="row">
          <b-form-input v-if="!row.item._overlap_group_header" style="width:250px" v-model="row.item.notes" v-on:blur="save_notes(row.item)" @keydown.enter.native="save_notes(row.item)"/>
        </template>

        <template slot="reprocess" slot-scope="row">
          <button
            v-if="enable_reprocess && !row.item._overlap_group_header"
            type="button"
            class="actuals-alert-action-btn"
            title="Resend customer pre-trip notification for this booking"
            aria-label="Resend customer pre-trip notification"
            @click.stop="reprocess_alert(row.item)"
          >
            <i class="fa fa-paper-plane" aria-hidden="true"></i>
          </button>
        </template>

        <template slot="retry" slot-scope="row">
          <button
            v-if="enable_reprocess && !row.item._overlap_group_header"
            type="button"
            class="actuals-alert-action-btn"
            title="Retry unsent customer pre-trip notification for this booking"
            aria-label="Retry unsent customer pre-trip notification"
            @click.stop="retry_unsent_alert(row.item)"
          >
            <i class="fa fa-repeat" aria-hidden="true"></i>
          </button>
        </template>

        <template slot="last_updated" slot-scope="row">
          {{row.item.last_updated | formatDateTimeCustom(summary.datetime_format)}}
        </template>

        <template slot="processed_time" slot-scope="row">
          {{row.item.processed_time | formatDateTimeCustom(summary.datetime_format)}}
        </template>

        <template slot="actuals_last_updated" slot-scope="row">
          {{row.item.actuals_last_updated | formatDateTimeCustom(summary.datetime_format)}}
        </template>

        <template slot="start_datetime" slot-scope="row">
          {{row.item.start_datetime | formatDateTimeCustom(summary.datetime_format)}}
        </template>

        <template slot="driver" slot-scope="row">
          <span v-if="!row.item._overlap_group_header" class="actuals-driver-cell">
            <span v-if="row.item.driver_overlap" class="overlap-badge" :title="overlap_driver_title(row.item)">⚠</span>
            <a
              v-if="driverHosLink(row.item)"
              target="_blank"
              class="actuals-telematics-link"
              :class="{'overlap-resource': row.item.driver_overlap}"
              :href="driverHosLink(row.item)"
              :title="'Open Samsara driver HOS report for ' + row.item.driver"
            >{{ row.item.driver | trim(20) }}</a>
            <span v-else :class="{'overlap-resource': row.item.driver_overlap}">{{ row.item.driver | trim(20) }}</span>
          </span>
        </template>

        <template slot="vehicle" slot-scope="row">
          <span v-if="row.item._overlap_group_header"></span>
          <span v-else>
            <span v-if="row.item.vehicle_overlap" class="overlap-badge" :title="overlap_vehicle_title(row.item)">⚠</span>
            <a
              v-if="row.item.vehicle && vehicleSamsaraLink(row.item)"
              target="_blank"
              class="actuals-telematics-link"
              :class="{'overlap-resource': row.item.vehicle_overlap}"
              :href="vehicleSamsaraLink(row.item)"
              :title="'Open Samsara vehicle trips for ' + row.item.vehicle"
            >{{ row.item.vehicle }}</a>
            <span v-else :class="{'overlap-resource': row.item.vehicle_overlap}">{{ row.item.vehicle }}</span>
          </span>
        </template>

        <template slot="pickup" slot-scope="row">
            {{row.item.pickup | trim(15)}}
        </template>

        <template slot="routeName" slot-scope="row">
          {{row.item.routeName | trim(30)}}
        </template>

        <template slot="finish_datetime" slot-scope="row">
          {{row.item.finish_datetime | formatDateTimeCustom(summary.datetime_format)}}
        </template>

        <template slot="delete" slot-scope="row">
          <div
            v-if="row.item._overlap_group_header"
            class="overlap-section-heading"
            :class="'overlap-section-heading--' + row.item._overlap_group_type"
          >
            {{ row.item._overlap_group_line }}
          </div>
          <div v-else style="font-weight: bold; color: darkred; padding:3px">
            &nbsp;<a @click="delete_item(row.item)">X</a>&nbsp;
          </div>
        </template>

        <template slot="details" slot-scope="row">
          <div
            v-if="row.item._overlap_group_header && overlap_header_field_key() === 'details'"
            class="overlap-section-heading"
            :class="'overlap-section-heading--' + row.item._overlap_group_type"
          >
            {{ row.item._overlap_group_line }}
          </div>
          <div v-else-if="row.item._overlap_group_header"></div>
          <div v-else>
            <b-button size="sm" @click="toggle_row_details(row)">
              {{ row.detailsShowing ? '-' : '+' }}
            </b-button>
          </div>
        </template>

        <template slot="row-details" slot-scope="row">
          <div v-if="row.item._overlap_group_header"></div>
          <b-card v-else style="text-align: left; border: 1px solid grey">
            <table width="100%">
              <tr>
                <th>Event</th>
                <th>Planned Time</th>
                <th>HOS Time</th>
                <th>HOS Delta</th>
                <th>Vehicle Actual Time</th>
                <th>Vehicle Delta</th>
                <th>Planned Location</th>
              </tr>
              <tr>
                <td>Start Time</td>
                <td>{{row.item.actual.movement.StartDateTime | formatDateTimeCustom(summary.datetime_format)}}</td>
                <td><span :class="{'hos-diff-time': hos_start_diff_highlight(row.item)}">{{row.item.actual.nearestLoginStartDateTime | formatDateTimeCustom(summary.datetime_format)}}</span></td>
                <td>{{get_time_diff(row.item.actual.plannedStartDateTime,row.item.actual.nearestLoginStartDateTime)}}</td>
                <td>{{row.item.actual.nearestLocationStartDateTime | formatDateTimeCustom(summary.datetime_format)}}</td>
                <td>{{get_time_diff(row.item.actual.plannedStartDateTime,row.item.actual.nearestLocationStartDateTime)}}</td>
                <td><span v-html="row.item.actual.startMapLink"/></td>
              </tr>
              <tr>
                <td>Position/Spot</td>
                <td>{{row.item.actual.movement.PositionDateTime | formatDateTimeCustom(summary.datetime_format)}}</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>{{row.item.actual.actualPositionDateTime | formatDateTimeCustom(summary.datetime_format)}}</td>
                <td>{{get_time_diff(row.item.actual.plannedPositionDateTime,row.item.actual.actualPositionDateTime)}}</td>
                <td><span v-html="row.item.actual.positionMapLink"/></td>
              </tr>
              <tr>
                <td>Pickup Time</td>
                <td>{{row.item.actual.movement.PickupDateTime | formatDateTimeCustom(summary.datetime_format)}}</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>{{row.item.actual.actualPickupDateTime | formatDateTimeCustom(summary.datetime_format)}}</td>
                <td>{{get_time_diff(row.item.actual.plannedPickupDateTime,row.item.actual.actualPickupDateTime)}}</td>
                <td><span v-html="row.item.actual.pickupMapLink"/></td>
              </tr>
              <tr>
                <td>Arrival Time</td>
                <td>{{row.item.actual.movement.ArrivalDateTime | formatDateTimeCustom(summary.datetime_format)}}</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>{{row.item.actual.actualArrivalDateTime | formatDateTimeCustom(summary.datetime_format)}}</td>
                <td>{{get_time_diff(row.item.actual.plannedArrivalDateTime,row.item.actual.actualArrivalDateTime)}}</td>
                <td><span v-html="row.item.actual.arrivalMapLink"/></td>
              </tr>
              <tr>
                <td>Leave Time</td>
                <td>{{row.item.actual.movement.LeaveDateTime | formatDateTimeCustom(summary.datetime_format)}}</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>{{row.item.actual.actualLeaveDateTime | formatDateTimeCustom(summary.datetime_format)}}</td>
                <td>{{get_time_diff(row.item.actual.plannedLeaveDateTime,row.item.actual.actualLeaveDateTime)}}</td>
                <td><span v-html="row.item.actual.leaveMapLink"/></td>
              </tr>
              <tr>
                <td>Back Time</td>
                <td>{{row.item.actual.movement.BackDateTime | formatDateTimeCustom(summary.datetime_format)}}</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>{{row.item.actual.actualBackDateTime | formatDateTimeCustom(summary.datetime_format)}}</td>
                <td>{{get_time_diff(row.item.actual.plannedBackDateTime,row.item.actual.actualBackDateTime)}}</td>
                <td><span v-html="row.item.actual.backMapLink"/></td>
              </tr>
              <tr>
                <td>Finish Time</td>
                <td>{{row.item.actual.movement.FinishDateTime | formatDateTimeCustom(summary.datetime_format)}}</td>
                <td><span :class="{'hos-diff-time': hos_finish_diff_highlight(row.item)}">{{row.item.actual.nearestLogoutFinishDateTime | formatDateTimeCustom(summary.datetime_format)}}</span></td>
                <td>{{get_time_diff(row.item.actual.plannedFinishDateTime,row.item.actual.nearestLogoutFinishDateTime)}}</td>
                <td>{{row.item.actual.nearestLocationFinishDateTime | formatDateTimeCustom(summary.datetime_format)}}</td>
                <td>{{get_time_diff(row.item.actual.plannedFinishDateTime,row.item.actual.nearestLocationFinishDateTime)}}</td>
                <td><span v-html="row.item.actual.finishMapLink"/></td>
              </tr>
            </table>
            <br/>
            <div v-html="row.item.actual.samsara_links"/>
            <span>
              <b>Map Links: </b>
              <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/vehicle-track/' + user.tenant + '/' + row.item.vehicle }">Track Vehicle</router-link>
              &nbsp;|&nbsp;
              <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/fleet-track/' + user.tenant + '/bookingType/' + row.item.bookingType }">Track '{{row.item.bookingType}}' Vehicles</router-link>
              &nbsp;|&nbsp;
              <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/fleet-track/' + user.tenant + '/clientID/' + row.item.clientID }">Track '{{row.item.clientID}}' Vehicles</router-link>
              &nbsp;|&nbsp;
              <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/fleet-track/' + user.tenant + '/pickup/' + row.item.pickup }">Track '{{row.item.pickup}}' Pickup</router-link>
              &nbsp;|&nbsp;
              <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/fleet-track/' + user.tenant + '/booking/' + row.item.charterID }">Track Booking '{{row.item.charterID}}' Vehicles</router-link>
              &nbsp;|&nbsp;
              <router-link target="_blank" style="font-weight: bold;" :to="{ path: '/fleet-track/' + user.tenant + '/movement/' + row.item.bookingID }">Track Movement '{{row.item.movementID}}' Vehicles</router-link>
              &nbsp;|&nbsp;
              <router-link style="font-weight: bold;" :to="{ path: '/vehicle_trip_history?vehicleId=' + row.item.vehicle }">Trip History</router-link>
            </span>

            <div class="row" style="margin: 10px 0px 0px 10px">
              <span style="padding: 10px; border: 1px solid black" v-if="row.item.actual.source_actualStartDateTime">
                <div><b>Calc Event Source:</b></div>
                <div>Start Time: {{row.item.actual.source_actualStartDateTime | nearest }}</div>
                <div>Finish Time: {{row.item.actual.source_actualFinishDateTime | nearest }}</div>
              </span>

              <span style="padding: 10px; border: 1px solid black" v-if="row.item.actual.nearestLoginStartDateTime || row.item.actual.nearestEngineOnStartDateTime || row.item.actual.nearestLocationStartDateTime">
                <b>Start Event Times:</b><br/>
                HOS On Duty: <span :class="{'hos-diff-time': hos_start_diff_highlight(row.item)}">{{row.item.actual.nearestLoginStartDateTime | formatDateTimeCustom(summary.datetime_format)}}</span><br/>
                DVIR Pre Trip Time: {{row.item.actual.nearestPreTripDVIRDateTime | formatDateTimeCustom(summary.datetime_format)}}<br/>
                Vehicle Key On: {{row.item.actual.nearestEngineOnStartDateTime | formatDateTimeCustom(summary.datetime_format)}}<br/>
                Vehicle Start Leave: {{row.item.actual.nearestLocationStartDateTime | formatDateTimeCustom(summary.datetime_format)}}
              </span>
              <span style="padding: 10px; border: 1px solid black" v-if="row.item.actual.nearestLogoutFinishDateTime || row.item.actual.nearestEngineOffFinishDateTime || row.item.actual.nearestLocationFinishDateTime">
                <b>Finish Event Times:</b><br/>
                HOS Off Duty: <span :class="{'hos-diff-time': hos_finish_diff_highlight(row.item)}">{{row.item.actual.nearestLogoutFinishDateTime | formatDateTimeCustom(summary.datetime_format)}}</span><br/>
                DVIR Post Trip Time: {{row.item.actual.nearestPostTripDVIRDateTime | formatDateTimeCustom(summary.datetime_format)}}<br/>
                Vehicle Key Off: {{row.item.actual.nearestEngineOffFinishDateTime | formatDateTimeCustom(summary.datetime_format)}}<br/>
                Vehicle Finish Arrival: {{row.item.actual.nearestLocationFinishDateTime | formatDateTimeCustom(summary.datetime_format)}}
              </span>
            </div>
            <div class="row" style="margin: 10px 0px 0px 10px">
              <span style="padding: 10px; border: 1px solid black" v-if="row.item.actual.actualTotalDistance">
                <b>Distances (miles)</b><br/>
                Planned Total: {{row.item.plannedDistance}}<br/>
                Trip Total: {{row.item.actual.actualTotalDistance}}<br/>
                Start Odometer: {{row.item.actual.actualStartOdometer}}<br/>
                End Odometer: {{row.item.actual.actualEndOdometer}}<br/>
                Odometer Total: {{row.item.actual.actualEndOdometer - row.item.actual.actualStartOdometer}}
              </span>
              <span style="padding: 10px; border: 1px solid black" v-if="row.item.actual.jurisdictionDistances">
                <b>State Distances (miles)</b><br/>
                <div v-for="dist in row.item.actual.jurisdictionDistances">
                  {{dist.jurisdiction}} : {{dist.distance}}m
                </div>
              </span>
              <span style="padding: 10px; border: 1px solid black" v-if="row.item.actual.actualLiveDistance">
                <b>Live & Dead Mileage (miles):</b><br/>
                Live Mileage: {{row.item.actual.actualLiveDistance}}<br/>
                Dead Mileage: {{row.item.actual.actualEmptyDistance}}{{row.item.actual.actualDeadDistance}}<br/>
              </span>
              <span style="padding: 10px; border: 1px solid black" v-if="row.item.actual.actualHOSOnDuration">
                <b>Driver HOS Activity (minutes):</b><br/>
                Total On Duty: {{row.item.actual.actualHOSOnDuration}} {{minutesToHours(row.item.actual.actualHOSOnDuration)}}<br/>
                Total Off Duty: {{row.item.actual.actualHOSOffDuration}} {{minutesToHours(row.item.actual.actualHOSOffDuration)}}<br/>
                Total Driving : {{row.item.actual.actualHOSDrivingDuration}} {{minutesToHours(row.item.actual.actualHOSDrivingDuration)}}<br/>
              </span>
            </div>
            <div style="text-align: left; margin-left: 10px; margin-top: 10px">
              <b-button size="sm" variant="info" title="Show Actuals Details"
                        @click="open_hos_modal(row.item)" :disabled="hos_modal_loading">
                <i class="fa fa-area-chart" aria-hidden="true"></i> Actuals Details
              </b-button>
              <b-button v-if="row.item.actual.file_history" size="sm" variant="info"
                        style="margin-left: 10px"
                        :title="row.item.actual.isFileHistoryVisible ? 'Hide booking file history' : 'Show booking file history'"
                        @click="toggle_div(row.item.actual,'isFileHistoryVisible')">
                <i class="fa fa-history" aria-hidden="true"></i>
                {{ row.item.actual.isFileHistoryVisible ? 'Hide' : 'Show' }} booking file history
              </b-button>
              <b-button v-if="row.item.actual.mvmt_html" size="sm" variant="info"
                        style="margin-left: 10px" title="Show CM movement"
                        @click="open_mvmt_modal(row.item.bookingID, row.item.actual.mvmt_html)">
                <i class="fa fa-file-text-o" aria-hidden="true"></i> Show CM movement
              </b-button>
              <table v-if="row.item.actual.file_history && row.item.actual.isFileHistoryVisible"
                     class="table table-sm table-bordered file-history-table"
                     style="width:auto; margin-top:10px">
                <thead>
                  <tr>
                    <th>Updated</th>
                    <th>Filename</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="entry in file_history_rows(row.item.actual.file_history)" :key="entry.filename + '|' + entry.time">
                    <td style="white-space:nowrap">{{ entry.time | formatDateTimeCustom(summary.datetime_format) }}</td>
                    <td style="white-space:nowrap">
                      <a target="_blank" :title="'download ' + entry.filename"
                         :href="`${api_url}bookings/downloadBooking?tenant=${user.tenant}&filename=${encodeURIComponent(entry.filename)}`">{{ entry.filename }}</a>
                    </td>
                    <td style="white-space:nowrap">
                      <a href="#" v-on:click.prevent="open_booking_modal(row.item.bookingID, entry.filename)">show booking</a>
                      <span v-if="!entry.isOldest">
                        &nbsp;|&nbsp;<a href="#" v-on:click.prevent="open_diff_modal(row.item.bookingID, entry.filename, row.item.actual.file_history)">show updates</a>
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-card>
        </template>
      </b-table>
      <b-row>
        <b-col>
          <b-pagination
              v-model="currentPage"
              :total-rows="items_row_count"
              :per-page="perPage"
              align="center"
          >
          </b-pagination>
        </b-col>
      </b-row>
    </div>

    <b-modal v-model="booking_modal_visible" :title="booking_modal_title" size="xl" ok-only scrollable>
      <div v-if="booking_modal_loading" class="text-center">
        <b-spinner/>
      </div>
      <div v-else v-html="booking_modal_html" style="text-align: left"/>
    </b-modal>

    <actuals-hos-timeline-modal
      :visible.sync="hos_modal_visible"
      :loading="hos_modal_loading"
      :error="hos_modal_error"
      :summary="hos_modal_summary"
      :events="hos_modal_events"
      :ignition-events="hos_modal_ignition_events"
      :feedback-item="hos_modal_item"
      :tenant="user.tenant"
      :feedback-enabled="feedback_audit_mode_enabled"
      :show-admin-actions="isEffectiveAdmin"
      :nav-prev-enabled="hos_modal_nav_has_prev"
      :nav-next-enabled="hos_modal_nav_has_next"
      :nav-label="hos_modal_nav_label"
      @prev="hos_modal_go_prev"
      @next="hos_modal_go_next"
      @feedback-saved="on_feedback_saved"
      @reload="reload_hos_modal"
      @manual-times-saved="filter_results"
      @showAlert="(msg, variant, timeout) => $emit('showAlert', msg, variant, timeout)"
      @hidden="reset_hos_modal"
    />

    <actuals-error-summary-modal
      :visible.sync="error_summary_modal_visible"
      :loading="error_summary_modal_loading"
      :error="error_summary_modal_error"
      :summary="error_summary_modal_summary"
      :error-categories="error_summary_modal_categories"
      :error-groups="error_summary_modal_groups"
      :troubleshooter-link="error_summary_troubleshooter_link"
      @hidden="reset_error_summary_modal"
    />

    <actuals-overlap-updates-modal
      :visible.sync="overlap_updates_modal_visible"
      :booking-label="overlap_updates_modal_booking_label"
      :updates="overlap_updates_modal_updates"
      @hidden="reset_overlap_updates_modal"
    />
  </div>
</template>

<script>

    import {api_url, delete_booking, get_actuals, get_actuals_hos_timeline, get_actuals_error_summary, save_booking_notes, reprocess_actuals, reprocess_alerts_for_booking, clear_actuals_feedback_for_date_range } from '../networking/apis'
    import {mapState} from "vuex";
    import moment from 'moment'
    import {justDate} from "@/helpers/dates";
    import {
      clampDateRangeForLargeFleet,
      configureTenantBaseOptions,
      isDateRangeExceedingLargeFleetLimit,
    } from '@/helpers/actuals_base_requirement'
    import { consecutiveEntriesOverlapMoreThanOneMinute } from '@/helpers/actuals_overlap'
    import { excludeSkippedUnlessSelected, filterActualsItemsByStatus } from '@/helpers/actuals_status_filter'
import { countActualsFeedback, matchesActualsFeedbackFilter, findActualsFeedbackRow, ACTUALS_FEEDBACK_FILTER_TOOLTIPS } from '@/helpers/actuals_feedback_filter'
import { isFeedbackAuditModeEnabled } from '@/helpers/feedback_audit_mode'
import { countActualsTripDuration, matchesActualsTripDurationFilter, ACTUALS_TRIP_DURATION_FILTER_TOOLTIPS } from '@/helpers/actuals_trip_duration_filter'
    import error_category_popover from '@/components/error_category_popover'
    import { categorizeError, listErrorCategories } from '@/helpers/actuals_error_categories'
    import actuals_troubleshooter_help from '@/components/actuals_troubleshooter_help'
    import actuals_hos_timeline_modal from '@/components/actuals_hos_timeline_modal'
    import actuals_error_summary_modal from '@/components/actuals_error_summary_modal'
    import actuals_overlap_updates_modal from '@/components/actuals_overlap_updates_modal'
    import overlap_correction_icon_btn from '@/components/overlap_correction_icon_btn'
    import actuals_feedback_controls from '@/components/actuals_feedback_controls'
    import { hasOverlapCorrection } from '@/helpers/overlap_updates_format'
    import { hosModalNavState, hosModalPageForIndex } from '@/helpers/actuals_hos_modal_nav'
    import { copyToClipboard } from '@/helpers/vsm_standalone_url'
    import axios from 'axios';

    export default {
      name: 'actuals',
      components: {
        'error-category-popover': error_category_popover,
        'actuals-troubleshooter-help': actuals_troubleshooter_help,
        'actuals-hos-timeline-modal': actuals_hos_timeline_modal,
        'actuals-error-summary-modal': actuals_error_summary_modal,
        'actuals-overlap-updates-modal': actuals_overlap_updates_modal,
        'overlap-correction-icon-btn': overlap_correction_icon_btn,
        'actuals-feedback-controls': actuals_feedback_controls,
      },

      data() {
        return {
          msg: 'actuals',
          items: [],
          hos_modal_nav_items: [],
          items_full: [],
          selected_status: null,
          selected_error_category: null,
          selected_feedback: null,
          selected_trip_duration: null,
          clearing_feedback: false,
          feedbackFilterTooltips: ACTUALS_FEEDBACK_FILTER_TOOLTIPS,
          tripDurationFilterTooltips: ACTUALS_TRIP_DURATION_FILTER_TOOLTIPS,
          show_extra_status_filters: false,
          summary: [],
          loading: true,
          company_options: null,
          vehicle_options: null,
          type_options: null,
          base_options: [],
          base_required: false,
          selected_vehicle: '',
          selected_company: '',
          selected_base: '',
          selected_type: '',
          details_fromDate: justDate(moment()),
          details_toDate: justDate(moment()),
          quick_range_options: [
            { value: 1, label: '1 Day' },
            { value: 3, label: '3 Day' },
            { value: 7, label: '7 Day' },
          ],
          totalRows: 1,
          currentPage: 1,
          perPage: 100,
          pages: [],
          actuals_filter: 'finish',
          booking_filter: '',
          booking_search: '',
          actuals_filter_options: [
            { value: 'finish', text: 'By Finish Date' },
            { value: 'start', text: 'By Start Date' },
            { value: 'overlap', text: 'Overlaps Range' },
          ],
          hos_diff_threshold_minutes: 30,
          hos_diff_threshold_options: [
            { value: 15, text: '15m' },
            { value: 30, text: '30m' },
            { value: 60, text: '60m' },
            { value: 120, text: '120m' },
          ],
          fields: [
            { key: 'delete', label: '', sortable: false},
            { key: 'details', label: '', sortable: false},
            { key: 'bookingID', label: 'ID', sortable: true, thStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' }},
            { key: 'charterID', label: 'CharterID', sortable: true},
            { key: 'movementID', label:'MvmtID', sortable: true},
            { key: 'vehicle', label:'Vehicle', sortable: true},
            { key: 'vehicle_type', label:'VehType', sortable: true},
            { key: 'driver', sortable: true, label: 'Driver'},
            { key: 'bookingType', label: 'Type', sortable: true},
            { key: 'bookingBase', label: 'Base', sortable: true},
            { key: 'startBase', label: 'Start', sortable: true},
            { key: 'finishBase', label: 'Finish', sortable: true},
            { key: 'pickup', label: 'Pickup', sortable: true},
            { key: 'destination', label: 'Destination', sortable: true},
            //{ key: 'furtherRequirements', label: 'Further Reqs', sortable: true},
            //{ key: 'pickupInstructions', label: 'Pickup Inst', sortable: true},
            { key: 'companyID', label: 'Company', sortable: true},
            { key: 'clientID', label: 'ClientID', sortable: true},
            { key: 'routeName', sortable: true},
            //{ key: 'seats', sortable: true},
            { key: 'start_datetime', label:'Start', sortable: true},
            { key: 'finish_datetime', label: 'Finish', sortable: true},
            { key: 'last_updated', label:'Booking Update', sortable: true},
            { key: 'processed_time', label:'Actuals Processed', sortable: true, thStyle: { width: '120px' }},
            { key: 'actuals_last_updated', label:'Actuals Update', sortable: true, thStyle: { width: '120px' }},
            //{ key: 'coach_manager_utc_day', label:'Start Date', sortable: true},
            //{ key: 'coach_manager_utc_finish_day', label:'Finish Date', sortable: true},
            //{ key: 'poc', sortable: true, thStyle: { width: "130px" }},
            { key: 'status_msg', label: 'Status Message', sortable: true, thStyle: { width: "450px", minWidth: '315px' }, thClass: 'actuals-status-msg-col', tdClass: 'actuals-status-msg-col' },
            { key: 'actuals_chart', label: '', sortable: false, thStyle: { width: "44px" }},
            { key: 'notes', label: 'Notes', sortable: true, thStyle: { width: "250px" }},
            { key: 'reprocess', label: 'Resend Cust Msgs', sortable: false, thStyle: { width: '72px', textAlign: 'center' }, tdClass: 'actuals-alert-action-col' },
            { key: 'retry', label: 'Retry Unsent Msgs', sortable: false, thStyle: { width: '72px', textAlign: 'center' }, tdClass: 'actuals-alert-action-col' },
          ],
          enable_reprocess: false,
          booking_modal_visible: false,
          booking_modal_title: '',
          booking_modal_html: '',
          booking_modal_loading: false,
          hos_modal_visible: false,
          hos_modal_loading: false,
          hos_modal_error: '',
          hos_modal_summary: {},
          hos_modal_events: [],
          hos_modal_ignition_events: [],
          hos_modal_item: null,
          error_summary_modal_visible: false,
          error_summary_modal_loading: false,
          error_summary_modal_error: '',
          error_summary_modal_summary: {},
          error_summary_modal_categories: [],
          error_summary_modal_groups: [],
          overlap_updates_modal_visible: false,
          overlap_updates_modal_item: null,
          overlap_colspan_observer: null,
          copied_booking_id: null,
          copied_booking_id_timer: null,
          options: {
            format: 'MM-DD-YYYY',
            useCurrent: true,
            minDate: moment().subtract(720, 'days'),
            maxDate: moment().add(180, 'days'),
            keepOpen: false,
            showClear: true,
            showClose: true,
            toolbarPlacement: 'top',
            widgetPositioning: {
              horizontal: 'left',
              vertical: 'bottom',
            },
          },

        }
      },

      async created() {
        this.fields_all = this.fields
        this.check_features()
      },
      methods: {
        async check_features() {
          this.check_cols()
          this.load_data()
        },
        async check_cols() {
          this.delete_enabled = this.user.role == 'customer-admin' || this.isEffectiveAdmin
          this.enable_reprocess = this.user.tenant.includes('aaahi')
          this.fields = this.fields_all
          if (!this.delete_enabled)
            this.fields = this.fields.filter(item => item.key != 'delete')
          if (!this.isEffectiveAdmin)
            this.fields = this.fields.filter(item => (
              item.key != 'bookingID'
              && item.key != 'poc'
              && item.key != 'reprocess'
              && item.key != 'retry'
            ))
          if (this.isEffectiveAdmin)
            this.fields = this.fields.filter(item => (
              item.key != 'pickup'
              && item.key != 'destination'
              && item.key != 'clientID'
            ))
          if (!this.enable_reprocess)
            this.fields = this.fields.filter(item => item.key != 'reprocess' && item.key != 'retry')
          if (!this.user.features[this.user.tenant].booking_notes_enabled)
            this.fields = this.fields.filter(item => item.key != 'notes')
        },
        async open_hos_modal(item) {
          if (!item || !item.bookingID)
            return
          this.hos_modal_item = item
          this.hos_modal_visible = true
          await this.load_hos_modal()
        },
        async load_hos_modal() {
          const item = this.hos_modal_item
          if (!item || !item.bookingID)
            return
          this.hos_modal_loading = true
          this.hos_modal_error = ''
          this.hos_modal_summary = {}
          this.hos_modal_events = []
          this.hos_modal_ignition_events = []
          try {
            const { value = {} } = await get_actuals_hos_timeline(this.user.tenant, item.bookingID, item.driver)
            const payload = (value.data && (value.data.data || value.data)) || {}
            this.hos_modal_error = payload.error || ''
            this.hos_modal_summary = payload.summary || {}
            this.hos_modal_events = payload.events || []
            this.hos_modal_ignition_events = payload.ignition_events || []
          } catch (e) {
            const resp = e.response && e.response.data
            const payload = resp && (resp.data || resp)
            this.hos_modal_error = (payload && payload.error) ||
              (resp && resp.message) ||
              (resp && resp.error) ||
              'error loading actuals details, try logging in again or contact support if needed'
          }
          this.hos_modal_loading = false
        },
        async reload_hos_modal() {
          await this.load_hos_modal()
        },
        async hos_modal_navigate(delta) {
          const { navItems, index } = this.hos_modal_nav
          const nextIdx = index + delta
          if (nextIdx < 0 || nextIdx >= navItems.length)
            return
          const page = hosModalPageForIndex(nextIdx, this.perPage)
          if (this.currentPage !== page)
            this.currentPage = page
          await this.open_hos_modal(navItems[nextIdx])
        },
        hos_modal_go_prev() {
          return this.hos_modal_navigate(-1)
        },
        hos_modal_go_next() {
          return this.hos_modal_navigate(1)
        },
        reset_hos_modal() {
          this.hos_modal_loading = false
          this.hos_modal_error = ''
          this.hos_modal_summary = {}
          this.hos_modal_events = []
          this.hos_modal_ignition_events = []
          this.hos_modal_item = null
        },
        async open_error_summary_modal() {
          this.error_summary_modal_visible = true
          this.error_summary_modal_loading = true
          this.error_summary_modal_error = ''
          this.error_summary_modal_summary = {}
          this.error_summary_modal_categories = []
          this.error_summary_modal_groups = []
          try {
            const from = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
            const to = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
            const { value: { data = {} }} = await get_actuals_error_summary(
              this.user.tenant,
              from,
              to,
              this.actuals_filter,
              this.selected_company,
              this.selected_type,
            )
            this.error_summary_modal_summary = data.summary || {}
            this.error_summary_modal_categories = data.error_categories || []
            this.error_summary_modal_groups = data.error_groups || []
          } catch (e) {
            this.error_summary_modal_error = 'error loading error summary, try logging in again or contact support if needed'
          }
          this.error_summary_modal_loading = false
        },
        reset_error_summary_modal() {
          this.error_summary_modal_loading = false
          this.error_summary_modal_error = ''
          this.error_summary_modal_summary = {}
          this.error_summary_modal_categories = []
          this.error_summary_modal_groups = []
        },
        async copy_booking_id(bookingID) {
          if (!bookingID)
            return
          try {
            await copyToClipboard(String(bookingID))
            this.copied_booking_id = bookingID
            if (this.copied_booking_id_timer)
              clearTimeout(this.copied_booking_id_timer)
            this.copied_booking_id_timer = setTimeout(() => {
              this.copied_booking_id = null
            }, 2000)
          } catch (e) {
            this.$emit('showAlert', 'unable to copy ID', 'danger', 3000)
          }
        },
        booking_search_route(bookingId) {
          return {
            name: 'actuals',
            query: { booking_id: bookingId },
          }
        },
        driverHosLink(item) {
          if (!item || !this.isSamsaraTenant)
            return null
          if (item.driver_hos_link)
            return item.driver_hos_link
          if (!this.summary || !this.summary.org_id || !item.samsara_driver_id)
            return null
          const endMs = this.driverHosEndMs(item)
          if (!endMs)
            return null
          return `https://cloud.samsara.com/o/${this.summary.org_id}/fleet/reports/hos/driver/driver/${item.samsara_driver_id}?end_ms=${endMs}`
        },
        vehicleSamsaraLink(item) {
          if (!item || !this.isSamsaraTenant || !item.vehicle)
            return null
          return item.samsara_vehicle_link || null
        },
        driverHosEndMs(item) {
          const finish = item && item.finish_datetime
          if (!finish)
            return null
          const finishDate = finish instanceof Date ? finish : new Date(finish)
          if (Number.isNaN(finishDate.getTime()))
            return null
          const dayStart = new Date(finishDate)
          dayStart.setHours(0, 0, 0, 0)
          return Math.floor(dayStart.getTime()) + 43199999 + 25200000
        },
        async reprocess_actuals() {
          try {
            if (confirm("are you sure you want to reprocess actuals for this date range and status?")) {
                this.$emit('showAlert', 'sending request to reprocess actuals', 'info', 5000)
                const {value: {data = []}} = await reprocess_actuals(this.user.tenant, this.actuals_filter, this.selected_company, this.selected_type, this.selected_status, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'), moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'))
                this.$emit('showAlert', 'reprocess request sent successfully', 'success', 5000)
              }
          } catch(e) {
            this.$emit('showAlert', 'error reprocessing actuals, try a smaller date range or contact support if needed', 'danger', 5000)
          }
        },
        resetTenantBases() {
          this.selected_base = ''
          this.base_required = false
          this.base_options = []
        },
        applyBaseOptions(baseOptions) {
          const { base_required, base_options } = configureTenantBaseOptions(baseOptions)
          this.base_required = base_required
          this.base_options = base_options
          if (this.base_required)
            this.applyLargeFleetDateRange('from')
        },
        applyLargeFleetDateRange(changedField) {
          if (!this.base_required)
            return
          const { fromDate, toDate } = clampDateRangeForLargeFleet(
            this.details_fromDate,
            this.details_toDate,
            changedField
          )
          this.details_fromDate = fromDate
          this.details_toDate = toDate
        },
        async search_by_date_range() {
          this.booking_search = ''
          if (this.$route.query.booking_id) {
            const query = { ...this.$route.query }
            delete query.booking_id
            await this.$router.replace({ query })
          }
          this.load_data()
        },
        async search_by_id() {
          this.booking_filter = ''
          this.load_data()
        },
        delete_item(delete_item) {
          if(confirm("Do you really want to delete booking: " + delete_item.bookingID + "?")) {
            delete_booking(this.user.tenant, delete_item.bookingID)
            this.load_data()
          }
        },
        async save_notes(item) {
          try {
            let json = {
              'booking_hash': item.bookingID,
              'type': 'actuals',
              'notes': item.notes
            }
            const {value: { data = [] } } = await save_booking_notes(this.user.tenant, json)
          } catch(e) {
            this.$emit('showAlert', 'error updating data, try logging in again or contact support if needed', 'danger', 5000)
          }

        },
        async load_data() {
          if (isDateRangeExceedingLargeFleetLimit(this.details_fromDate, this.details_toDate, this.base_required)) {
            this.applyLargeFleetDateRange('to')
            this.$emit('showAlert', 'please limit date range to 7 days or less', 'warning', 5000)
            return
          }
          this.loading = true
          try {
            if (this.$route.query.booking_id && !this.booking_search)
              this.booking_search = this.$route.query.booking_id

            try {
              const {value: {data = []}} = await get_actuals(this.user.tenant, this.actuals_filter, this.booking_search, this.selected_company, this.selected_type, moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'), moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'))
              this.summary = data.summary
              moment.tz.setDefault(this.summary.timezone)
              this.items = data.results
              this.items_full = data.results
              this.detect_overlaps(this.items_full)

              if (data.summary.companies && data.summary.companies.length > 2)
                this.company_options = data.summary.companies

              this.vehicle_options = data.summary.vehicles
              this.type_options = data.summary.types
              if (data.summary && data.summary.bases)
                this.applyBaseOptions(data.summary.bases)
              this.filter_results()

              if (this.summary.total_cnt != this.items_full.length) {
                this.$emit('showAlert', 'too many results to process for ' + this.booking_search + ', showing partial results', 'warning', 10000)
              }
            } catch(e){
              this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
            }
          } catch(e){
            this.$emit('showAlert', 'error retrieving data, try logging in again or contact support if needed', 'danger', 5000)
          }
          this.loading = false
        },
        async reprocess_alert(item) {
          if (confirm("Are you sure you want to resend customer pre-trip notifications for this booking?")) {
            reprocess_alerts_for_booking(this.user.tenant, "CustomerTripNotification", item.bookingID)
            this.$emit('showAlert', 'reprocessing alert', 'info', 5000)
          }
        },
        async retry_unsent_alert(item) {
          if (confirm("Are you sure you want retry unsent customer pre-trip notifications for this booking?")) {
            reprocess_alerts_for_booking(this.user.tenant, "CustomerTripNotificationRetry", item.bookingID)
            this.$emit('showAlert', 'retry unsent alert', 'info', 5000)
          }
        },
        async download(){
          this.$emit('showAlert', 'processing download request', 'info', 10000)
          axios.defaults.headers.common.Accept = {}
          axios.post(
              `${api_url}bookings/actuals/download`,
              {
                tenant: this.user.tenant,
                filter: this.actuals_filter,
                company: this.selected_company,
                type: this.selected_type,
                status: this.selected_status,
                fromDate: moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
                toDate: moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
              }, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('jwt')}`,
                  Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                },
                responseType: 'blob'
              }
          ).then((response) => {
            axios.defaults.headers.common.Accept = 'application/json'
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute(
                'download',
                `actuals-${this.user.tenant}-${this.details_fromDate}-${this.details_toDate}.csv`
            )
            document.body.appendChild(link)
            link.click()
            this.$emit('showAlert', 'download complete', 'success', 5000)
          }).catch(e => {
            axios.defaults.headers.common.Accept = 'application/json'
            this.$emit('showAlert', 'actuals data could not be downloaded.', 'danger')
          })
        },
        shift_detail_graph_date(shift, unit) {
          if (unit === 'months') {
            if (this.base_required) {
              this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift * 7, 'days').format('MM-DD-YYYY')
              this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift * 7, 'days').format('MM-DD-YYYY')
              this.applyLargeFleetDateRange('from')
            } else {
              const month = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'months')
              let from = month.clone().startOf('month')
              let to = month.clone().endOf('month')
              const min = moment(this.options.minDate)
              const max = moment(this.options.maxDate)
              if (from.isBefore(min, 'day'))
                from = min.clone()
              if (to.isAfter(max, 'day'))
                to = max.clone()
              if (from.isAfter(to, 'day'))
                from = to.clone()
              this.details_fromDate = from.format('MM-DD-YYYY')
              this.details_toDate = to.format('MM-DD-YYYY')
            }
          } else {
            this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
            this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
          }
          this.booking_id_filter = ''
        },
        set_quick_range(days) {
          this.details_toDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(days - 1, 'days').format('MM-DD-YYYY')
        },
        get_time_diff(time1, time2) {
          if (time1 != null && time2 != null)
            return Math.round((new Date(time2) - new Date(time1)) / (1000 * 60)) + 'm';
          return ''
        },
        hos_delta_minutes(planned, actual) {
          if (planned == null || actual == null)
            return null
          const plannedMs = new Date(planned).getTime()
          const actualMs = new Date(actual).getTime()
          if (Number.isNaN(plannedMs) || Number.isNaN(actualMs))
            return null
          return Math.round((actualMs - plannedMs) / 60000)
        },
        hos_start_diff_over_threshold(item) {
          if (!item || item.status !== 'success' || !item.actual)
            return false
          const delta = this.hos_delta_minutes(item.actual.plannedStartDateTime, item.actual.nearestLoginStartDateTime)
          return delta != null && Math.abs(delta) > this.hos_diff_threshold_minutes
        },
        hos_finish_diff_over_threshold(item) {
          if (!item || item.status !== 'success' || !item.actual)
            return false
          const delta = this.hos_delta_minutes(item.actual.plannedFinishDateTime, item.actual.nearestLogoutFinishDateTime)
          return delta != null && Math.abs(delta) > this.hos_diff_threshold_minutes
        },
        is_hos_diff_over_threshold(item) {
          return this.hos_start_diff_over_threshold(item) || this.hos_finish_diff_over_threshold(item)
        },
        hos_start_diff_highlight(item) {
          return this.selected_status === 'hos_diff_over_threshold' && this.hos_start_diff_over_threshold(item)
        },
        hos_finish_diff_highlight(item) {
          return this.selected_status === 'hos_diff_over_threshold' && this.hos_finish_diff_over_threshold(item)
        },
        hos_time_highlight_classes(item, side, overlapHighlight) {
          const classes = {}
          if (overlapHighlight)
            classes['overlap-time'] = true
          if (side === 'start' && this.hos_start_diff_highlight(item))
            classes['hos-diff-time'] = true
          if (side === 'finish' && this.hos_finish_diff_highlight(item))
            classes['hos-diff-time'] = true
          return classes
        },
        toggle_div(item,field) {
          item[field] = !item[field];
          this.$forceUpdate();
        },
        async load_booking_modal(path, title, params) {
          this.booking_modal_title = title
          this.booking_modal_html = ''
          this.booking_modal_loading = true
          this.booking_modal_visible = true
          try {
            const response = await axios.get(`${api_url}${path}`, {
              params: params,
              headers: {
                Authorization: `Bearer ${localStorage.getItem('jwt')}`
              }
            })
            this.booking_modal_html = response.data
          } catch (e) {
            this.booking_modal_html = '<pre>error loading content</pre>'
          }
          this.booking_modal_loading = false
        },
        open_diff_modal(bookingID, filename, history) {
          this.load_booking_modal('bookings/showDiff', 'Booking Diff: ' + filename, {
            tenant: this.user.tenant,
            bookingID: bookingID,
            filename: filename,
            history: history,
          })
        },
        open_booking_modal(bookingID, filename) {
          this.load_booking_modal('bookings/showBooking', 'Booking: ' + filename, {
            tenant: this.user.tenant,
            bookingID: bookingID,
            filename: filename,
          })
        },
        open_mvmt_modal(bookingID, mvmt_html) {
          this.booking_modal_title = 'CM Movement: ' + bookingID
          this.booking_modal_html = mvmt_html
          this.booking_modal_loading = false
          this.booking_modal_visible = true
        },
        file_history_rows(history) {
          if (!history) return []
          const segments = history.split(',').map(s => s.trim()).filter(Boolean)
          const rows = segments.map((segment, i) => {
            const parts = segment.split(' : ')
            return {
              filename: (parts[0] || '').replace(' ', ''),
              time: parts[1] || '',
              isOldest: i === 0,
            }
          })
          return rows.slice().reverse()
        },
        get_row_color(item, type) {
            if (!item || type !== 'row') return
            if (item._overlap_group_header)
              return 'overlap-group-header-row'
            let rowClass = item.status || ''
            if (item.driver_overlap && item.vehicle_overlap)
              rowClass += ' overlap-both'
            else if (item.driver_overlap)
              rowClass += ' overlap-driver'
            else if (item.vehicle_overlap)
              rowClass += ' overlap-vehicle'
            return rowClass.trim()
        },
        overlap_header_field_key() {
          return (this.table_fields[0] && this.table_fields[0].key) || 'details'
        },
        get_overlap_td_class(fieldKey, item) {
          const classes = []
          if (fieldKey === 'details' || fieldKey === 'actuals_chart')
            classes.push('actuals-details-td')
          if (fieldKey === 'bookingID')
            classes.push('actuals-id-td')
          if (item && item._overlap_group_header)
            classes.push(fieldKey === this.overlap_header_field_key() ? 'overlap-group-header-cell' : 'overlap-group-header-empty')
          return classes.join(' ')
        },
        get_actuals_table_element() {
          const tableRef = this.$refs.actualsCmTable
          if (!tableRef)
            return null
          const root = tableRef.$el || tableRef
          if (!root)
            return null
          const tables = root.querySelectorAll ? root.querySelectorAll('table.actuals-cm-table, table.table') : []
          for (let i = 0; i < tables.length; i++) {
            if (tables[i].querySelector('tbody tr.overlap-group-header-row'))
              return tables[i]
          }
          if (root.tagName === 'TABLE')
            return root
          return root.querySelector('table.actuals-cm-table') || root.querySelector('table')
        },
        apply_overlap_header_colspan_row(tr, colCount) {
          const headerCell = Array.from(tr.children).find(td => td.querySelector('.overlap-section-heading'))
          if (!headerCell)
            return
          Array.from(tr.children).forEach(td => {
            if (td !== headerCell)
              td.remove()
          })
          if (tr.firstElementChild !== headerCell)
            tr.insertBefore(headerCell, tr.firstElementChild)
          headerCell.colSpan = colCount
          headerCell.classList.add('overlap-group-header-cell')
          headerCell.classList.remove('overlap-group-header-empty')
        },
        stop_overlap_colspan_observer() {
          if (this.overlap_colspan_observer) {
            this.overlap_colspan_observer.disconnect()
            this.overlap_colspan_observer = null
          }
        },
        start_overlap_colspan_observer() {
          this.stop_overlap_colspan_observer()
          if (!this.show_overlap_group_headers)
            return
          this.$nextTick(() => {
            const table = this.get_actuals_table_element()
            const tbody = table && table.querySelector('tbody')
            if (!tbody)
              return
            this.overlap_colspan_observer = new MutationObserver(() => {
              this.apply_overlap_header_colspans()
            })
            this.overlap_colspan_observer.observe(tbody, { childList: true, subtree: true })
            this.apply_overlap_header_colspans()
          })
        },
        toggle_row_details(row) {
          row.toggleDetails()
          this.schedule_overlap_header_colspans()
        },
        schedule_overlap_header_colspans() {
          if (!this.show_overlap_group_headers)
            return
          const run = () => this.apply_overlap_header_colspans()
          this.$nextTick(() => {
            this.$nextTick(() => {
              run()
              if (typeof window !== 'undefined' && window.requestAnimationFrame)
                window.requestAnimationFrame(run)
            })
          })
        },
        apply_overlap_header_colspans() {
          if (!this.show_overlap_group_headers || this._applying_overlap_colspan)
            return
          this._applying_overlap_colspan = true
          try {
            const table = this.get_actuals_table_element()
            if (!table)
              return
            const colCount = this.table_fields.length
            table.querySelectorAll('tbody tr.overlap-group-header-row').forEach(tr => {
              this.apply_overlap_header_colspan_row(tr, colCount)
            })
          } finally {
            this._applying_overlap_colspan = false
          }
        },
        detect_overlaps(items) {
          if (!items || !items.length)
            return
          for (const item of items) {
            if (item._status_msg_base === undefined)
              item._status_msg_base = item.status_msg || ''
            item.driver_overlap = false
            item.vehicle_overlap = false
            item.driver_overlap_details = []
            item.vehicle_overlap_details = []
            item.driver_overlap_highlight_start = false
            item.driver_overlap_highlight_end = false
            item.vehicle_overlap_highlight_start = false
            item.vehicle_overlap_highlight_end = false
            item.driver_overlap_group = null
            item.vehicle_overlap_group = null
          }
          this.mark_resource_overlaps(items, 'driver', 'nearestLoginStartDateTime', 'nearestLogoutFinishDateTime', 'driver_overlap', 'driver_overlap_details', 'driver_overlap_group', 'driver_overlap_highlight_start', 'driver_overlap_highlight_end')
          this.mark_resource_overlaps(items, 'vehicle', 'nearestLocationStartDateTime', 'nearestLocationFinishDateTime', 'vehicle_overlap', 'vehicle_overlap_details', 'vehicle_overlap_group', 'vehicle_overlap_highlight_start', 'vehicle_overlap_highlight_end')
          for (const item of items)
            item.status_msg = item._status_msg_base
        },
        departure_calendar_day(item) {
          if (!item || !item.start_datetime)
            return null
          const m = moment(item.start_datetime)
          return m.isValid() ? m.format('YYYY-MM-DD') : null
        },
        get_resource_entries(items, resourceKey, startField, endField) {
          const groups = {}
          for (const item of items) {
            if (item.status !== 'success')
              continue
            const resource = item[resourceKey]
            const actual = item.actual
            if (!resource || !actual)
              continue
            const start = actual[startField]
            const end = actual[endField]
            if (!start || !end)
              continue
            const sortKey = item.start_datetime || start
            if (!groups[resource])
              groups[resource] = []
            groups[resource].push({ item, start, end, sortKey })
          }
          for (const resource of Object.keys(groups))
            groups[resource].sort((a, b) => new Date(a.sortKey) - new Date(b.sortKey))
          return groups
        },
        times_overlap_by_minute(currStart, prevEnd) {
          return consecutiveEntriesOverlapMoreThanOneMinute(prevEnd, currStart)
        },
        mark_resource_overlaps(items, resourceKey, startField, endField, overlapFlag, overlapDetailsFlag, overlapGroupField, highlightStartKey, highlightEndKey) {
          const groups = this.get_resource_entries(items, resourceKey, startField, endField)
          const parent = new Map()
          const find = (item) => {
            if (!parent.has(item))
              parent.set(item, item)
            if (parent.get(item) !== item)
              parent.set(item, find(parent.get(item)))
            return parent.get(item)
          }
          const union = (a, b) => {
            parent.set(find(a), find(b))
          }
          for (const resource of Object.keys(groups)) {
            const entries = groups[resource]
            if (entries.length < 2)
              continue
            for (let i = 1; i < entries.length; i++) {
              const prev = entries[i - 1]
              const current = entries[i]
              if (!this.times_overlap_by_minute(current.start, prev.end))
                continue
              union(prev.item, current.item)
              prev.item[overlapFlag] = true
              current.item[overlapFlag] = true
              prev.item[highlightEndKey] = true
              current.item[highlightStartKey] = true
              this.add_overlap_detail(prev.item, overlapDetailsFlag, current.item.bookingID, current.start, current.end, prev.start, prev.end)
              this.add_overlap_detail(current.item, overlapDetailsFlag, prev.item.bookingID, prev.start, prev.end, current.start, current.end)
            }
            const components = new Map()
            for (const entry of entries) {
              if (!entry.item[overlapFlag])
                continue
              const root = find(entry.item)
              if (!components.has(root))
                components.set(root, [])
              const members = components.get(root)
              if (members.indexOf(entry.item) === -1)
                members.push(entry.item)
            }
            for (const members of components.values()) {
              if (members.length < 2)
                continue
              const days = [...new Set(members.map(m => this.departure_calendar_day(m)).filter(Boolean))].sort()
              const group = {
                key: resourceKey + '|' + resource + '|' + days.join(','),
                resource,
                resourceKey,
                days,
                members,
              }
              for (const member of members)
                member[overlapGroupField] = group
            }
          }
        },
        add_overlap_detail(item, overlapDetailsFlag, otherBookingID, otherStart, otherEnd, thisStart, thisEnd) {
          if (!otherBookingID)
            return
          const details = item[overlapDetailsFlag]
          if (details.some(d => d.otherBookingID === otherBookingID && d.otherStart === otherStart && d.otherEnd === otherEnd))
            return
          details.push({ otherBookingID, otherStart, otherEnd, thisStart, thisEnd })
        },
        format_overlap_time(value) {
          if (!value)
            return '?'
          const m = moment(value)
          return m.isValid() ? m.format('MM/DD HH:mm') : String(value)
        },
        formatActualTimeForStatus(value) {
          if (!value)
            return ''
          const m = moment(value)
          return m.isValid() ? m.format('MM/DD hh:mm A') : ''
        },
        format_success_status_lines(item) {
          const actual = item && item.actual
          if (!actual)
            return []

          const lines = []
          const addRange = (key, label, start, end) => {
            if (!start && !end)
              return
            lines.push({ key, type: 'range', label, start, end })
          }

          addRange('calced', 'Calced', actual.actualStartDateTime, actual.actualFinishDateTime)
          addRange('driver', 'Driver', actual.nearestLoginStartDateTime, actual.nearestLogoutFinishDateTime)
          addRange('vehicle', 'Vehicle', actual.nearestLocationStartDateTime, actual.nearestLocationFinishDateTime)
          addRange('dvir', 'DVIR', actual.nearestPreTripDVIRDateTime, actual.nearestPostTripDVIRDateTime)

          const meta = []
          if (actual.actualTotalDistance != null && actual.actualTotalDistance !== '')
            meta.push('<b>Distance:</b> ' + actual.actualTotalDistance + ' mi')
          if (actual.actualTotalDuration != null && actual.actualTotalDuration !== '') {
            const hrs = (Number(actual.actualTotalDuration) / 60).toFixed(2)
            meta.push('<b>Duration:</b> ' + actual.actualTotalDuration + 'm (' + hrs + ' hrs)')
          }
          if (meta.length)
            lines.push({ key: 'meta', type: 'text', text: meta.join(' · ') })

          return lines
        },
        success_status_lines_without_overlap(item) {
          return this.format_success_status_lines(item).filter(line =>
            line.type !== 'range' || (line.label !== 'Driver' && line.label !== 'Vehicle'),
          )
        },
        success_status_extras(item) {
          const html = item._status_msg_base || item.status_msg || ''
          if (!html)
            return []
          const extras = []
          const clientRefMatch = html.match(/<b>clientRef2:<\/b>[\s\S]*?(?=<br\s*\/?>|$)/i)
          if (clientRefMatch)
            extras.push(clientRefMatch[0].trim())
          return extras
        },
        non_success_status_msg_parts(item) {
          const html = item._status_msg_base || item.status_msg || ''
          if (!html)
            return []
          return html.split(',').map(part => part.trim()).filter(part => part.length > 0)
        },
        show_overlap_time_highlights(item) {
          if (!item || !item.actual)
            return false
          const actual = item.actual
          const hasDriverLine = actual.nearestLoginStartDateTime || actual.nearestLogoutFinishDateTime
          const hasVehicleLine = actual.nearestLocationStartDateTime || actual.nearestLocationFinishDateTime
          if (hasDriverLine && (item.driver_overlap_highlight_start || item.driver_overlap_highlight_end))
            return true
          if (hasVehicleLine && (item.vehicle_overlap_highlight_start || item.vehicle_overlap_highlight_end))
            return true
          return false
        },
        overlap_display_row_key(item) {
          if (!item)
            return ''
          return [item.bookingID, item.movementID, item.start_datetime]
            .map(v => (v == null ? '' : String(v)))
            .join('|')
        },
        build_overlap_display_items(items, resourceKey, overlapFlag, overlapGroupField) {
          const groupsByKey = {}
          for (const item of items) {
            if (!item[overlapFlag] || !item[overlapGroupField])
              continue
            const group = item[overlapGroupField]
            if (!groupsByKey[group.key])
              groupsByKey[group.key] = { group, items: [], seen: new Set() }
            const bucket = groupsByKey[group.key]
            const rowKey = this.overlap_display_row_key(item)
            if (bucket.seen.has(rowKey))
              continue
            bucket.seen.add(rowKey)
            bucket.items.push(item)
          }
          const sorted = []
          const keys = Object.keys(groupsByKey).sort((a, b) => {
            const minA = Math.min(...groupsByKey[a].items.map(i => new Date(i.start_datetime).getTime()))
            const minB = Math.min(...groupsByKey[b].items.map(i => new Date(i.start_datetime).getTime()))
            return minA - minB
          })
          for (const key of keys) {
            const { group, items: members } = groupsByKey[key]
            if (members.length < 2)
              continue
            members.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))
            const resourceName = group.resource || members[0][resourceKey] || members[0].vehicle_type || ''
            sorted.push({
              _overlap_group_header: true,
              _overlap_group_type: resourceKey === 'driver' ? 'driver' : 'vehicle',
              _overlap_group_line: resourceName,
            })
            sorted.push(...members)
          }
          return sorted
        },
        overlap_driver_title(item) {
          if (!item.driver_overlap_details || !item.driver_overlap_details.length)
            return 'Driver HOS on/off duty times overlap another booking in this date range'
          return item.driver_overlap_details.map(d =>
            'Driver overlap booking ' + d.otherBookingID + ' (' + this.format_overlap_time(d.otherStart) + ' – ' + this.format_overlap_time(d.otherEnd) + ')'
          ).join('; ')
        },
        overlap_vehicle_title(item) {
          if (!item.vehicle_overlap_details || !item.vehicle_overlap_details.length)
            return 'Vehicle start/finish location times overlap another booking in this date range'
          return item.vehicle_overlap_details.map(d =>
            'Vehicle overlap booking ' + d.otherBookingID + ' (' + this.format_overlap_time(d.otherStart) + ' – ' + this.format_overlap_time(d.otherEnd) + ')'
          ).join('; ')
        },
        has_overlap_correction(item) {
          return hasOverlapCorrection(item)
        },
        open_overlap_updates_modal(item) {
          this.overlap_updates_modal_item = item
          this.overlap_updates_modal_visible = true
        },
        reset_overlap_updates_modal() {
          this.overlap_updates_modal_item = null
        },
        overlap_correction_booking_label(item) {
          if (!item)
            return ''
          return item.charterID || item.bookingID || ''
        },
        is_overlap_section_header(item) {
          return !!(item && item._overlap_group_header)
        },
        strip_overlap_section_headers(items) {
          return (items || []).filter(item => !this.is_overlap_section_header(item))
        },
        filter_by_status(status) {
          if (status == null || this.selected_status == status) {
            this.selected_status = null
          } else {
            this.selected_status = status
          }
          if (this.selected_status !== 'error')
            this.selected_error_category = null
          this.currentPage = 1
          this.filter_results()
        },
        on_feedback_saved(event) {
          const payload = event || {}
          const target = findActualsFeedbackRow(this.items_full, payload.item, false)
          if (target && target.actual) {
            if (payload.rating == null) {
              this.$delete(target.actual, 'feedback')
              this.$delete(target.actual, 'feedback_comment')
            } else {
              this.$set(target.actual, 'feedback', payload.rating)
              if (payload.rating === 'concern' && payload.comment)
                this.$set(target.actual, 'feedback_comment', payload.comment)
              else
                this.$delete(target.actual, 'feedback_comment')
            }
          }
          this.filter_results()
        },
        filter_by_feedback(feedback) {
          if (feedback == null || this.selected_feedback === feedback) {
            this.selected_feedback = null
          } else {
            this.selected_feedback = feedback
          }
          this.currentPage = 1
          this.filter_results()
        },
        filter_by_trip_duration(duration) {
          if (duration == null || this.selected_trip_duration === duration) {
            this.selected_trip_duration = null
          } else {
            this.selected_trip_duration = duration
          }
          this.currentPage = 1
          this.filter_results()
        },
        async confirm_clear_feedback_for_date_range() {
          if (this.clearing_feedback || !this.feedback_audit_mode_enabled)
            return
          const fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
          const toDate = moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
          const ratedCount = this.feedback_positive_cnt + this.feedback_negative_cnt + this.feedback_concern_cnt
          const rangeLabel = `${this.details_fromDate} to ${this.details_toDate}`
          const msg = ratedCount > 0
            ? `Delete feedback on ${ratedCount} actuals from ${rangeLabel}?`
            : `Delete all actuals feedback from ${rangeLabel}?`
          if (!confirm(`${msg} This cannot be undone from the actuals page.`))
            return
          this.clearing_feedback = true
          try {
            const {value: {data = {}}} = await clear_actuals_feedback_for_date_range(this.user.tenant, fromDate, toDate)
            const deleted = data.deleted_count || 0
            this.selected_feedback = null
            this.clear_feedback_from_loaded_rows()
            this.$emit('showAlert', `Cleared feedback on ${deleted} actuals`, 'success', 4000)
            if (deleted > 0)
              await this.load_data()
          } catch (e) {
            console.warn('Failed to clear actuals feedback:', e)
            this.$emit('showAlert', 'error clearing feedback, try again or contact support', 'danger', 5000)
          } finally {
            this.clearing_feedback = false
          }
        },
        clear_feedback_from_loaded_rows() {
          for (const item of this.items_full || []) {
            if (item && item.actual) {
              this.$delete(item.actual, 'feedback')
              this.$delete(item.actual, 'feedback_comment')
            }
          }
          this.filter_results()
        },
        filter_results() {
          if (!this.show_overlap_filters
            && (this.selected_status === 'driver_overlap'
              || this.selected_status === 'vehicle_overlap'
              || this.selected_status === 'overlap_correction')) {
            this.selected_status = null
          }
          let items = excludeSkippedUnlessSelected(this.items_full, this.selected_status)
          if (!items)
            return
          if (this.booking_filter) {
            const val = this.booking_filter

            if (this.booking_filter.indexOf("=") != -1) {
              const field = this.booking_filter.split("=")[0]
              const value = this.booking_filter.split("=")[1]
              if (field == 'driver')
                items = items.filter(item => item.driver == value)
              else if (field == 'vehicle')
                items = items.filter(item => item.vehicle == value)
            }
            else {
              items = items.filter(item => (this.contains(item.bookingID, val) ||
                  this.contains(item.status_msg, val) ||
                  this.contains(item.driver, val) ||
                  this.contains(item.vehicle, val) ||
                  this.contains(item.routeName, val) ||
                  this.contains(item.pickup, val) ||
                  this.contains(item.destination, val) ||
                  this.contains(item.bookingBase, val) ||
                  this.contains(item.startBase, val) ||
                  this.contains(item.finishBase, val) ||
                  this.contains(item.bookingType, val) ||
                  this.contains(item.fileHistory, val) ||
                  this.contains(item.clientID, val)))
            }
          }
          if (this.selected_company) {
            items = items.filter(item => this.contains(item.companyID, this.selected_company))
          }
          if (this.selected_vehicle) {
            items = items.filter(item => this.contains(item.vehicle, this.selected_vehicle))
          }
          if (this.selected_base) {
            items = items.filter(item => this.contains(item.bookingBase, this.selected_base) || this.contains(item.startBase, this.selected_base) || this.contains(item.finishBase, this.selected_base))
          }
          if (this.selected_status != null) {
            if (this.selected_status === 'driver_overlap') {
              this.items = this.build_overlap_display_items(items, 'driver', 'driver_overlap', 'driver_overlap_group')
            } else if (this.selected_status === 'vehicle_overlap') {
              this.items = this.build_overlap_display_items(items, 'vehicle', 'vehicle_overlap', 'vehicle_overlap_group')
            } else if (this.selected_status === 'hos_diff_over_threshold') {
              this.items = items.filter(item => this.is_hos_diff_over_threshold(item))
            } else if (this.selected_status === 'overlap_correction') {
              this.items = items.filter(item => hasOverlapCorrection(item))
            } else {
              this.items = items.filter(item => item.status == this.selected_status)
            }
          } else {
            this.items = items
          }
          let navItems = this.items
          if (this.selected_trip_duration != null) {
            this.items = this.items.filter(item => matchesActualsTripDurationFilter(item, this.selected_trip_duration, false))
          }
          if (this.selected_feedback != null && this.feedback_audit_mode_enabled) {
            this.items = this.items.filter(item => matchesActualsFeedbackFilter(item, this.selected_feedback))
          }
          if (this.selected_error_category && !this.show_overlap_group_headers) {
            this.items = this.items.filter(item => this.error_category_ids_for_item(item).includes(this.selected_error_category))
            navItems = navItems.filter(item => this.error_category_ids_for_item(item).includes(this.selected_error_category))
          }
          if (!this.show_overlap_group_headers) {
            this.items = this.strip_overlap_section_headers(this.items)
            this.hos_modal_nav_items = this.strip_overlap_section_headers(navItems)
          } else {
            this.hos_modal_nav_items = navItems
            this.schedule_overlap_header_colspans()
          }
        },
        minutesToHours(minutes) {
          if (minutes == null || minutes === 0) return ''
          return '(' + (minutes / 60).toFixed(2) + 'hrs)'
        },
        contains(val1, val2) {
          if (val1 != null) {
            return val1.toLowerCase().indexOf(val2.toLowerCase()) != -1;
          }
          return false;
        },
        error_category_ids_for_item(item) {
          if (!item || item.status !== 'error')
            return []
          const ids = new Set()
          for (const rawPart of this.non_success_status_msg_parts(item)) {
            const text = String(rawPart).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
            for (const sub of text.split('|').map(s => s.trim()).filter(s => s.length)) {
              ids.add(categorizeError(sub, 'cm').id)
            }
          }
          return Array.from(ids)
        }
      },
      computed:{
        ...mapState({
          user: state => state.profile.user,
          viewAs: state => state.profile.view_as,
        }),
        overlap_updates_modal_updates() {
          const item = this.overlap_updates_modal_item
          if (!item || !item.overlap_updates)
            return []
          return item.overlap_updates
        },
        overlap_updates_modal_booking_label() {
          return this.overlap_correction_booking_label(this.overlap_updates_modal_item)
        },
        error_category_options() {
          const counts = {}
          for (const item of (this.items_full || [])) {
            for (const id of this.error_category_ids_for_item(item))
              counts[id] = (counts[id] || 0) + 1
          }
          const labels = {}
          for (const c of listErrorCategories('cm'))
            labels[c.id] = c.category
          const opts = Object.keys(counts)
            .sort((a, b) => counts[b] - counts[a])
            .map(id => ({ value: id, text: (labels[id] || id) + ' (' + counts[id] + ')' }))
          return [{ value: null, text: 'All error categories' }, ...opts]
        },
        isEffectiveAdmin() {
          return this.user && this.user.role === 'admin' && this.viewAs !== 'non-admin'
        },
        isSamsaraTenant() {
          const type = (this.user && this.user.telematics_types && this.user.telematics_types[this.user.tenant])
            || (this.summary && this.summary.telematics_type)
            || ''
          return type === 'SAMSARA' || type === ''
        },
        show_base_select() {
          return this.base_required || (this.base_options && this.base_options.length > 2)
        },
        quick_range_active_days() {
          const from = moment(this.details_fromDate, 'MM-DD-YYYY')
          const to = moment(this.details_toDate, 'MM-DD-YYYY')
          return to.diff(from, 'days') + 1
        },
        api_url() {
          return api_url
        },
        success_cnt() {
          return this.items_full.filter(item => item.status == 'success').length
        },
        error_cnt() {
          return this.items_full.filter(item => item.status == 'error').length
        },
        cancelled_cnt() {
          return this.items_full.filter(item => item.status == 'cancelled').length
        },
        skipped_cnt() {
          return this.items_full.filter(item => item.status == 'skipped').length
        },
        unprocessed_cnt() {
          return this.items_full.filter(item => item.status == 'unprocessed').length
        },
        driver_overlap_cnt() {
          return this.items_full.filter(item => item.driver_overlap).length
        },
        vehicle_overlap_cnt() {
          return this.items_full.filter(item => item.vehicle_overlap).length
        },
        overlap_correction_cnt() {
          return this.items_full.filter(item => hasOverlapCorrection(item)).length
        },
        show_overlap_filters() {
          return this.driver_overlap_cnt > 0 || this.vehicle_overlap_cnt > 0 || this.overlap_correction_cnt > 0
        },
        hos_modal_nav() {
          return hosModalNavState(this.hos_modal_nav_items, this.hos_modal_item, false)
        },
        hos_modal_nav_has_prev() {
          return this.hos_modal_nav.hasPrev
        },
        hos_modal_nav_has_next() {
          return this.hos_modal_nav.hasNext
        },
        hos_modal_nav_label() {
          return this.hos_modal_nav.label
        },
        hos_diff_over_threshold_cnt() {
          return this.items_full.filter(item => this.is_hos_diff_over_threshold(item)).length
        },
        items_for_feedback_counts() {
          return filterActualsItemsByStatus(this.items_full, this.selected_status, {
            isHosDiffOverThreshold: item => this.is_hos_diff_over_threshold(item),
          })
        },
        items_for_trip_duration_counts() {
          return this.items_for_feedback_counts
        },
        trip_single_cnt() {
          return countActualsTripDuration(this.items_for_trip_duration_counts, 'single', false)
        },
        trip_multiday_cnt() {
          return countActualsTripDuration(this.items_for_trip_duration_counts, 'multiday', false)
        },
        feedback_positive_cnt() {
          return countActualsFeedback(this.items_for_feedback_counts, 'positive')
        },
        feedback_negative_cnt() {
          return countActualsFeedback(this.items_for_feedback_counts, 'negative')
        },
        feedback_concern_cnt() {
          return countActualsFeedback(this.items_for_feedback_counts, 'concern')
        },
        feedback_none_cnt() {
          return countActualsFeedback(this.items_for_feedback_counts, 'none')
        },
        feedback_audit_mode_enabled() {
          return isFeedbackAuditModeEnabled(this.user)
        },
        error_summary_troubleshooter_link() {
          return {
            path: '/actuals_troubleshooter',
            query: {
              source: 'cm',
              fromDate: this.details_fromDate,
              toDate: this.details_toDate,
              filter: this.actuals_filter,
              company: this.selected_company,
              type: this.selected_type,
            },
          }
        },
        table_fields() {
          let fields = this.fields
          if (!this.isEffectiveAdmin)
            fields = fields.filter(field => field.key !== 'processed_time' && field.key !== 'actuals_last_updated')
          return fields.map(field => ({
            ...field,
            tdClass: (value, key, item) => this.get_overlap_td_class(field.key, item),
          }))
        },
        show_overlap_group_headers() {
          return this.selected_status === 'driver_overlap' || this.selected_status === 'vehicle_overlap'
        },
        actuals_table_key() {
          return this.selected_status || 'all'
        },
        actuals_sticky_header() {
          return this.items_row_count > 0 ? '70vh' : false
        },
        items_row_count() {
          return (this.items || []).filter(item => !item._overlap_group_header).length
        },
      },
      updated() {
        this.schedule_overlap_header_colspans()
      },
      beforeDestroy() {
        this.stop_overlap_colspan_observer()
        if (this.copied_booking_id_timer)
          clearTimeout(this.copied_booking_id_timer)
      },
      watch: {
        user: {
          deep: true,
          handler() {
            this.resetTenantBases()
            this.load_data()
          }
        },
        viewAs() {
          this.check_cols()
        },
        details_toDate() {
          if (moment(this.details_fromDate, 'MM-DD-YYYY') > moment(this.details_toDate, 'MM-DD-YYYY'))
            this.details_toDate = this.details_fromDate
          else
            this.applyLargeFleetDateRange('to')
        },
        details_fromDate() {
          if (moment(this.details_fromDate, 'MM-DD-YYYY') > moment(this.details_toDate, 'MM-DD-YYYY'))
            this.details_toDate = this.details_fromDate
          else
            this.applyLargeFleetDateRange('from')
        },
        booking_filter() {
          this.filter_results()
        },
        selected_base() {
          this.filter_results()
        },
        selected_vehicle() {
          this.filter_results()
        },
        selected_company() {
          this.filter_results()
        },
        selected_error_category() {
          this.filter_results()
        },
        hos_diff_threshold_minutes() {
          if (this.selected_status === 'hos_diff_over_threshold')
            this.filter_results()
        },
        currentPage() {
          this.schedule_overlap_header_colspans()
        },
        fields() {
          this.schedule_overlap_header_colspans()
        },
        items() {
          this.schedule_overlap_header_colspans()
        },
        show_overlap_group_headers(active) {
          if (active)
            this.start_overlap_colspan_observer()
          else
            this.stop_overlap_colspan_observer()
        },
        actuals_table_key() {
          if (this.show_overlap_group_headers)
            this.start_overlap_colspan_observer()
        },
      }
    }
</script>

<style>

.actuals-cm-status-filter-row {
  position: relative;
  width: 100%;
}
.actuals-error-filter-group {
  position: absolute;
  right: 0;
  top: 0;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  font-weight: normal;
  flex-shrink: 0;
  z-index: 1;
}
.actuals-error-filter-label {
  font-weight: 600;
  margin-right: 8px;
  color: #4a6785;
}
.actuals-error-filter-select {
  display: inline-block;
  width: auto;
  min-width: 280px;
  vertical-align: middle;
}
.actuals-error-filter-clear {
  margin-left: 6px;
  padding: 0 6px;
}

.error { background-color: rgba(255,0,0,0.25)}
.success { background-color: hsla(120,100%,75%,0.3)}
.unprocessed { background-color: hsla(200,40%,80%,0.3) }
.cancelled { background-color: hsla(290,60%,70%,0.3) }
.skipped { background-color: hsla(190,90%,80%,0.3) }
.overlap-driver { background-color: hsla(210,85%,75%,0.45) }
.overlap-vehicle { background-color: hsla(35,100%,70%,0.45) }
.overlap-both { background-color: hsla(270,55%,78%,0.45) }

.error td { background-color: rgba(255,0,0,0.25)}
.success td {background-color: hsla(120,100%,75%,0.3)}
.unprocessed td { background-color: hsla(200,40%,80%,0.3) }
.cancelled td { background-color: hsla(290,60%,70%,0.3) }
.skipped td { background-color: hsla(190,90%,80%,0.3) }
.overlap-driver td { background-color: hsla(210,85%,75%,0.45) }
.overlap-vehicle td { background-color: hsla(35,100%,70%,0.45) }
.overlap-both td { background-color: hsla(270,55%,78%,0.45) }
.overlap-badge {
  color: #b45309;
  font-weight: bold;
  margin-right: 2px;
}
.overlap-resource {
  font-weight: bold;
  text-decoration: underline;
  text-decoration-color: #b45309;
}
.overlap-time {
  font-weight: bold;
  color: #c0392b;
}
.hos-diff-time {
  font-weight: bold;
  color: #c0392b;
}
.actuals-id-cell {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.actuals-id-copy {
  white-space: nowrap;
  font-weight: bold;
  flex-shrink: 0;
}
.actuals-id-text {
  font-size: 0.75em;
  font-weight: normal;
  word-break: break-all;
}
.actuals-cm-table td.actuals-id-td {
  min-width: 100px;
  max-width: 100px;
  width: 100px;
  white-space: normal;
  vertical-align: top;
}
.actuals-id-link {
  color: inherit;
  text-decoration: underline;
  text-decoration-color: #b45309;
}
.actuals-id-link:hover {
  color: var(--fi-action-primary);
  text-decoration-color: var(--fi-action-primary);
}
.actuals-telematics-link {
  font-weight: bold;
  color: inherit;
  text-decoration: underline;
  text-decoration-color: #b45309;
}
.actuals-telematics-link:hover {
  color: var(--fi-action-primary);
  text-decoration-color: var(--fi-action-primary);
}
.actuals-status-msg-body {
  text-align: left;
}
.actuals-driver-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
}
.actuals-status-msg-line {
  line-height: 1.35;
}
.actuals-status-msg-label {
  display: inline-block;
  min-width: 4.5em;
  font-weight: 600;
}
.actuals-details-icon-btn {
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: #3182bd;
  font-size: 22px;
  line-height: 1;
  width: 32px;
  height: 32px;
  cursor: pointer;
}
.actuals-details-icon-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.actuals-details-icon-btn:hover:not(:disabled) {
  color: #0c3d5c;
}
.actuals-alert-action-col {
  text-align: center;
  vertical-align: middle;
}
.actuals-alert-action-btn {
  padding: 0;
  border: none;
  background: transparent;
  color: #6c757d;
  font-size: 16px;
  line-height: 1;
  width: 28px;
  height: 28px;
  cursor: pointer;
}
.actuals-alert-action-btn:hover {
  color: #3182bd;
}
.actuals-cm-table td.actuals-details-td {
  vertical-align: middle;
  text-align: center;
}
.actuals-details-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}
.actuals-details-cell--stacked {
  flex-direction: column;
  gap: 2px;
}
.actuals-cm-table tbody tr.overlap-group-header-row {
  background-color: #f8f9fa !important;
}
.actuals-cm-table tbody tr.overlap-group-header-row td.overlap-group-header-empty {
  display: none;
}
.actuals-cm-table tbody tr.overlap-group-header-row td.overlap-group-header-cell {
  padding: 0 !important;
  border-top: 3px solid #6c757d !important;
  border-bottom: 1px solid #dee2e6 !important;
  background: transparent !important;
}
.overlap-section-heading {
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 10px 14px;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.4;
  white-space: normal;
  word-break: break-word;
}
.overlap-section-heading--driver {
  background-color: hsla(210, 85%, 75%, 0.55);
  border-left: 5px solid #3182bd;
  color: #0c3d5c;
}
.overlap-section-heading--vehicle {
  background-color: hsla(35, 100%, 70%, 0.55);
  border-left: 5px solid #e6a23c;
  color: #7c4a03;
}


div.row:nth-child(even) {
  background-color: #f2f2f2;
}

tr:nth-child(even) {
  background-color: #f2f2f2;
}

.header {
  font-weight: bold;
}

.actuals-cm-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px 8px;
  font-size: 14px;
  font-weight: bold;
  margin: 14px 8px 10px;
  position: relative;
}
.actuals-cm-toolbar .toolbar-date-shifts {
  flex-shrink: 0;
}
.actuals-cm-toolbar .actuals-quick-range {
  flex-shrink: 0;
}
.actuals-cm-toolbar .toolbar-date-shift-btn {
  padding: 0.15rem 0.35rem;
  line-height: 1;
  min-width: 1.65rem;
}
.actuals-cm-toolbar .toolbar-date-shift-btn .fa {
  font-size: 13px;
  font-weight: bold;
  vertical-align: middle;
}
.actuals-cm-toolbar .toolbar-date {
  font-size: 14px;
  font-weight: bold;
  width: 108px;
  padding: 0.2rem 0.45rem;
  height: calc(1.5em + 0.5rem + 2px);
}
.actuals-cm-toolbar .toolbar-select {
  font-size: 14px;
  font-weight: bold;
  width: 150px;
}
.actuals-cm-toolbar .toolbar-company { width: 160px; }
.actuals-cm-toolbar .toolbar-type { width: 130px; }
.actuals-cm-toolbar .toolbar-vehicle { width: 120px; }
.actuals-cm-toolbar .toolbar-base { width: 130px; }
.actuals-cm-toolbar .toolbar-input { width: 108px; }
.actuals-cm-toolbar .toolbar-label {
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  color: #6c757d;
}
.actuals-cm-toolbar .toolbar-btn { flex-shrink: 0; }
.actuals-cm-toolbar .toolbar-group {
  display: inline-flex;
  align-items: center;
  gap: 4px 6px;
  flex-shrink: 0;
}

.actuals-cm-filter-area {
  margin: 12px 8px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.actuals-cm-meta-line {
  text-align: center;
  font-size: 13px;
  margin-bottom: 10px;
}
.actuals-filter-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 6px 8px;
  font-weight: bold;
}
.actuals-cm-filter-chips {
  font-size: 14px;
}
.actuals-feedback-filter-group {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.actuals-cm-filter-area + div {
  margin-top: 12px;
}
.filter-expander {
  cursor: pointer;
  color: #6c757d;
  padding: 0 5px;
  user-select: none;
  font-size: 14px;
  line-height: 1;
}
.filter-expander:hover {
  color: #343a40;
}
.actuals-feedback-filter-group .actuals-filter-label,
.actuals-feedback-filter-group .actuals-chip,
.actuals-trip-duration-filter-group .actuals-filter-label,
.actuals-trip-duration-filter-group .actuals-chip {
  font-size: 12px;
  font-weight: 600;
}
.actuals-feedback-filter-group .actuals-chip,
.actuals-trip-duration-filter-group .actuals-chip {
  padding: 2px 8px;
}
.actuals-filter-label {
  margin-right: 2px;
  white-space: nowrap;
  color: #6c757d;
  font-weight: 600;
  font-size: 12px;
}
.actuals-filter-sep {
  color: #adb5bd;
  user-select: none;
  font-size: 13px;
}
.actuals-chip {
  display: inline-block;
  border: 1px solid #ced4da;
  background-color: #f8f9fa;
  color: #212529;
  border-radius: 999px;
  padding: 3px 10px;
  white-space: nowrap;
  line-height: 1.25;
  font-size: 14px;
}
.actuals-chip-clickable {
  cursor: pointer;
}
.actuals-chip-clickable:hover {
  filter: brightness(0.97);
}
.actuals-chip-selected:not(.actuals-chip-total) {
  border: 2px solid #dc3545 !important;
  box-shadow: 0 0 0 1px rgba(220, 53, 69, 0.2);
  padding: 2px 9px;
}
.actuals-chip-total.actuals-chip-selected {
  border: 1px solid #ced4da !important;
  box-shadow: none !important;
  padding: 3px 10px;
}
.actuals-chip.success { background-color: hsla(120,100%,75%,0.55); border-color: #7dcc7d; }
.actuals-chip.error { background-color: rgba(255,0,0,0.2); border-color: #e88; }
.actuals-chip.unprocessed { background-color: hsla(200,40%,80%,0.55); border-color: #9bb; }
.actuals-chip.cancelled { background-color: hsla(290,60%,70%,0.45); border-color: #b9b; }
.actuals-chip.skipped { background-color: hsla(190,90%,80%,0.55); border-color: #8cc; }
.actuals-chip.overlap-driver { background-color: hsla(210,85%,75%,0.65); border-color: #6baed6; color: #0c3d5c; }
.actuals-chip.overlap-vehicle { background-color: hsla(35,100%,70%,0.65); border-color: #e6a23c; color: #7c4a03; }
.actuals-chip.overlap-correction { background-color: hsla(211, 100%, 93%, 0.95); border-color: #0d6efd; color: #0a58ca; }
.actuals-chip.hos-diff { background-color: hsla(45,100%,80%,0.65); border-color: #d4a017; color: #6b4f00; }
.actuals-chip.feedback-positive { background-color: hsla(120,100%,75%,0.45); border-color: #198754; color: #0f5132; }
.actuals-chip.feedback-negative { background-color: rgba(255,0,0,0.15); border-color: #dc3545; color: #842029; }
.actuals-chip.feedback-concern { background-color: hsla(45,100%,90%,0.55); border-color: #b58100; color: #664d03; }
.actuals-chip.feedback-none { background-color: #f1f3f5; border-color: #adb5bd; color: #495057; }
.actuals-feedback-chip i {
  margin-right: 4px;
}
.actuals-cm-hos-diff-threshold {
  width: auto;
  max-width: 100px;
  min-width: 0;
  flex: 0 0 auto;
  font-size: 13px !important;
  font-weight: 600;
  padding: 2px 1.25rem 2px 6px !important;
  height: auto !important;
  line-height: 1.25;
  background-position: right 0.35rem center;
  background-size: 8px 10px;
}
.table td {
  padding: .1rem !important;
}

.mvmt-table td {
  vertical-align: middle !important;
}

.file-history-table th,
.file-history-table td {
  padding: 5px 5px !important;
  vertical-align: middle !important;
}

.file-history-table th {
  background-color: #f2f2f2;
  font-weight: 600;
}

.file-history-table tr td,
.file-history-table tr th {
  padding: 5px 5px !important;
}

</style>

